import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MessageSquare, User, Briefcase, Calendar, Edit2, Save, X, Download, FileText, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import ErrorBox from 'components/ErrorBox';
import Card from 'components/Card';
import {
  getGeneralQuestionnaireResponses,
  updateGeneralQuestionnaireNotes
} from 'api/apiHelper';

interface Response {
  id: number;
  question_order: number;
  question_text: string;
  answer_text: string;
  attachment_url?: string | null;
  answered_at: string;
}

interface QuestionnaireSession {
  id: number;
  test_token: string;
  status: string;
  applicant_name: string;
  applicant_email: string;
  job_title: string;
  started_at: string | null;
  completed_at: string | null;
  reviewer_notes: string;
}

const GeneralQuestionnaireReviewDetail: React.FC = () => {
  const { testToken } = useParams<{ testToken: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [session, setSession] = useState<QuestionnaireSession | null>(null);
  const [responses, setResponses] = useState<Response[]>([]);
  const [reviewerNotes, setReviewerNotes] = useState<string>('');
  const [isEditingNotes, setIsEditingNotes] = useState<boolean>(false);
  const [savingNotes, setSavingNotes] = useState<boolean>(false);

  useEffect(() => {
    if (testToken) {
      fetchQuestionnaireData();
    }
  }, [testToken]);

  const fetchQuestionnaireData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await getGeneralQuestionnaireResponses(testToken!);
      console.log('Questionnaire data:', response.data);
      
      setSession(response.data.questionnaire_session);
      setResponses(response.data.responses || []);
      setReviewerNotes(response.data.questionnaire_session.reviewer_notes || '');
    } catch (error: any) {
      console.error('Error fetching questionnaire data:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load questionnaire data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!testToken) return;

    setSavingNotes(true);
    try {
      await updateGeneralQuestionnaireNotes(testToken, reviewerNotes);
      toast.success('Notes saved successfully');
      setIsEditingNotes(false);
    } catch (error: any) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <Backdrop message="Loading questionnaire responses..." />;
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorBox errorMessage={error} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header Card */}
      {session && (
        <Card extra="p-6 bg-white dark:bg-navy-800 rounded-xl shadow-sm">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center">
                <User className="w-8 h-8 text-brand-600 dark:text-brand-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
                  {session.applicant_name}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {session.applicant_email}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500 dark:text-gray-400">Position</p>
              <p className="text-lg font-semibold text-navy-700 dark:text-white">
                {session.job_title}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-navy-700">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Status</p>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                session.status === 'completed'
                  ? 'bg-green-100 text-green-800'
                  : session.status === 'in_progress'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {session.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Started At</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">
                {formatDate(session.started_at)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Completed At</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">
                {formatDate(session.completed_at)}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Reviewer Notes Card */}
      {session && (
        <Card extra="p-6 bg-white dark:bg-navy-800 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
              Reviewer Notes
            </h3>
            {!isEditingNotes ? (
              <button
                onClick={() => setIsEditingNotes(true)}
                className="flex items-center px-3 py-1.5 text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Notes
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setIsEditingNotes(false);
                    setReviewerNotes(session.reviewer_notes || '');
                  }}
                  className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  className="flex items-center px-3 py-1.5 text-sm bg-brand-500 hover:bg-brand-600 text-white rounded-lg disabled:opacity-50"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingNotes ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>
          {isEditingNotes ? (
            <textarea
              value={reviewerNotes}
              onChange={(e) => setReviewerNotes(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 dark:border-navy-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-navy-700 dark:text-white"
              placeholder="Add your notes about this candidate's responses..."
            />
          ) : (
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {reviewerNotes || 'No notes added yet.'}
            </p>
          )}
        </Card>
      )}

      {/* Responses */}
      <Card extra="p-6 bg-white dark:bg-navy-800 rounded-xl shadow-sm">
        <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-6">
          Candidate Responses
        </h3>
        
        {responses.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              No responses submitted yet.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {responses
              .sort((a, b) => a.question_order - b.question_order)
              .map((response) => (
                <div
                  key={response.id}
                  className="border border-gray-200 dark:border-navy-700 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start mb-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400 font-semibold mr-4 flex-shrink-0">
                      {response.question_order}
                    </span>
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-navy-700 dark:text-white mb-2">
                        {response.question_text}
                      </h4>
                      <div className="mt-3 p-4 bg-gray-50 dark:bg-navy-700 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 font-medium">
                          Answer:
                        </p>
                        <p className="text-gray-800 dark:text-white whitespace-pre-wrap leading-relaxed">
                          {response.answer_text || <span className="text-gray-400 italic">No answer provided</span>}
                        </p>
                      </div>

                      {/* Attachment Display */}
                      {response.attachment_url && (
                        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              {response.attachment_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                <ImageIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3" />
                              ) : (
                                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3" />
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Attachment
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {response.attachment_url.match(/\.pdf$/i) ? 'PDF Document' : 'Image File'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <a
                                href={response.attachment_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors"
                              >
                                <ExternalLink className="w-4 h-4 mr-1.5" />
                                View
                              </a>
                              <a
                                href={response.attachment_url}
                                download
                                className="flex items-center px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
                              >
                                <Download className="w-4 h-4 mr-1.5" />
                                Download
                              </a>
                            </div>
                          </div>
                          {response.attachment_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                            <div className="mt-3">
                              <img
                                src={response.attachment_url}
                                alt="Attachment preview"
                                className="max-w-full h-auto rounded-lg border border-gray-200 dark:border-gray-700"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                        Answered on: {formatDate(response.answered_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default GeneralQuestionnaireReviewDetail;
